import React from "react";
import classNames from "classnames";
import EventListener from "react-event-listener";
import getMuiTheme from "material-ui/styles/getMuiTheme";
import MuiThemeProvider from "material-ui/styles/MuiThemeProvider";
import { StripeProvider } from "react-stripe-elements";

import BuyTicketsOverlay from "../app/components/overlays/buy-tickets.jsx";
import QuickOrderOverlay from "../app/components/overlays/quick-order.jsx";
import BuyPassesOverlay from "../app/components/overlays/buy-passes.jsx";
import EditPaymentMethodOverlay from "../app/components/overlays/edit-payment-method.jsx";
import Login from "../app/components/login.jsx";
import VirtualPlayerOverlay from "../app/components/overlays/virtual-player.jsx";
import ScanTicketsOverlay from "../app/components/overlays/scan-tickets.jsx";

import theme from "../app/theme";

import { createHistory, useQueries } from "history";
const { createLocation } = useQueries(createHistory)();

const PassThroughComponent = ({ children }) => <div>{children}</div>;

// For resizing/width detector
const SMALL = 1;
const MEDIUM = 2;
const LARGE = 3;
const largeWidth = 992;
const mediumWidth = 768;
const resizeInterval = 166; // Corresponds to 10 frames at 60 Hz.

class WidgetContainer extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      width: null,
      location: null,
      showTicketsOverlay: null,
      showPassesOverlay: null,
      showQuickOrderOverlay: null,
      showLoginOverlay: null,
      showPaymentUpdateOverlay: null,
      ticketsOverlayOnBack: null,
      showVirtualPlayerOverlay: null,
      showScanTicketsOverlay: null
    };

    this._goBack = this._goBack.bind(this);
    this._resetRouter = this._resetRouter.bind(this);
    this._updateRoute = this._updateRoute.bind(this);

    this.handleResize = this.handleResize.bind(this);
    this.updateWidth = this.updateWidth.bind(this);

    // Public methods
    this.setTicketsOverlay = this.setTicketsOverlay.bind(this);
  }

  static get childContextTypes() {
    return {
      muiTheme: React.PropTypes.object,
      router: React.PropTypes.object,
      tenant: React.PropTypes.object,
      appSettings: React.PropTypes.object,
      isLarge: React.PropTypes.bool,
      isMedium: React.PropTypes.bool,
      isSmall: React.PropTypes.bool,
      portalClassName: React.PropTypes.string
    };
  }

  getChildContext() {
    return {
      router: {
        push: loc => this._updateRoute(loc, "PUSH"),
        replace: loc => this._updateRoute(loc, "REPLACE"),
        goBack: () => this._goBack(),
        createHref: l => l,
        isActive: l => {
          if (this.state.location) {
            let curr = this.state.location;
            if (curr.pathname !== l.pathname) {
              return false;
            }
            // Deep compare query params
            if (l.query) {
              if (!curr.query) {
                return false;
              }
              for (let key in l.query) {
                if (l.query[key] !== curr.query[key]) {
                  return false;
                }
              }
            }
            return true;
          }
          return false;
        },
        getCurrentLocation: () => this.state.location
      },
      tenant: window.EVENTIVE_CONFIG,
      appSettings: this.props.appSettings || {},
      isLarge: this.state.width === LARGE,
      isMedium: this.state.width === MEDIUM,
      isSmall: this.state.width === SMALL,
      portalClassName: classNames(
        "eventive-widget-container",
        window.EVENTIVE_CONFIG.sundance_login && "Eventive--Sundance"
      )
    };
  }

  componentDidMount() {
    this.updateWidth();
  }

  componentWillUnmount() {
    clearTimeout(this.deferTimer);
  }

  handleResize() {
    clearTimeout(this.deferTimer);
    this.deferTimer = setTimeout(() => {
      this.updateWidth();
    }, resizeInterval);
  }

  updateWidth() {
    const innerWidth = window.innerWidth;
    let width;

    if (innerWidth >= largeWidth) {
      width = LARGE;
    } else if (innerWidth >= mediumWidth) {
      width = MEDIUM;
    } else {
      // innerWidth < 768
      width = SMALL;
    }

    if (width !== this.state.width) {
      this.setState({
        width: width
      });
    }
  }

  // Public method - for hijack
  setTicketsOverlay(eventId) {
    this._updateRoute(`/schedule/${eventId}/tickets`);
  }

  _goBack() {
    if (this.state.ticketsOverlayOnBack) {
      this.setTicketsOverlay(this.state.ticketsOverlayOnBack);
    } else {
      this._resetRouter();
    }
  }

  _resetRouter() {
    // Before updating, fire any close events for previously visible routes
    if (this.state.showTicketsOverlay) {
      BuyTicketsOverlay.willTransitionAway();
    }
    if (this.state.showQuickOrderOverlay) {
      QuickOrderOverlay.willTransitionAway();
    }

    this.setState({
      location: null,
      showTicketsOverlay: false,
      showQuickOrderOverlay: false,
      showPassesOverlay: false,
      showLoginOverlay: false,
      showPaymentUpdateOverlay: false,
      ticketsOverlayOnBack: null,
      showVirtualPlayerOverlay: false,
      showScanTicketsOverlay: false
    });
  }

  _updateRoute(location, action = "PUSH") {
    location = createLocation(location);
    location.action = action;

    if (/\/schedule\/([^/]*?)\/tickets$/.test(location.pathname)) {
      let eventId = location.pathname.split("/")[2];
      BuyTicketsOverlay.willTransitionTo(
        { params: { eventId }, location: location },
        () => {},
        () => {
          this._resetRouter();
          this.setState({ location, showTicketsOverlay: eventId });
        }
      );
    } else if (/\/passes\/buy\/([^/]*?)$/.test(location.pathname)) {
      let code = location.pathname.split("/")[3];
      BuyPassesOverlay.willTransitionTo(
        { params: { code }, location: location },
        () => {},
        () => {
          this._resetRouter();
          this.setState({ location, showPassesOverlay: code });
        }
      );
    } else if (/\/schedule\/([^/]*?)\/quick\/event$/.test(location.pathname)) {
      let eventId = location.pathname.split("/")[2];
      QuickOrderOverlay.willTransitionTo(
        { params: { eventId }, location: location },
        () => {},
        () => {
          this._resetRouter();
          this.setState({ location, showQuickOrderOverlay: eventId });
        }
      );
    } else if (/\/tickets\/([^/]*?)$/.test(location.pathname)) {
      let eventId = location.pathname.split("/")[2];
      ScanTicketsOverlay.willTransitionTo(
        { params: { eventId }, location: location },
        () => {},
        () => {
          this._resetRouter();
          this.setState({ location, showScanTicketsOverlay: eventId });
        }
      );
    } else if (
      location.pathname === "/tickets" &&
      window.__EVENTIVE_EVERYWHERE_HAS_UNIVERSAL
    ) {
      // Hacky: When navigating to a modal on the tickets page (transfer or cancel),
      // we just push the location update through since TicketListItem will handle
      // rendering the modal.
      this._resetRouter();
      this.setState({ location });
    } else if (/\/tickets\/([^/]*?)$/.test(location.pathname)) {
      let eventId = location.pathname.split("/")[2];
      ScanTicketsOverlay.willTransitionTo(
        { params: { eventId }, location: location },
        () => {},
        () => {
          this._resetRouter();
          this.setState({ location, showScanTicketsOverlay: eventId });
        }
      );
    } else if (/\/_virtual_play\/([^/]*?)$/.test(location.pathname)) {
      let virtualScreeningId = location.pathname.split("/")[2];
      this._resetRouter();
      this.setState({ location, showVirtualPlayerOverlay: virtualScreeningId });
    } else if (location.pathname === "/login") {
      let _oldTicketsOverlay;
      if (this.state.showTicketsOverlay) {
        _oldTicketsOverlay = this.state.showTicketsOverlay;
      }
      this._resetRouter();
      this.setState({
        location,
        showLoginOverlay: true,
        ticketsOverlayOnBack: _oldTicketsOverlay
      });
    } else if (
      location.pathname === "/welcome" ||
      location.pathname === "/schedule" ||
      // Correctly handle Sundance SIO sales page
      location.pathname === "/sio-thankyou" ||
      /\/schedule\/([^/]*?)/.test(location.pathname)
    ) {
      this._resetRouter();
    } else if (location.pathname === "/account/payment") {
      this._resetRouter();
      this.setState({ location, showPaymentUpdateOverlay: true });
    } else {
      this._resetRouter();
      let win = window.open(
        this.props.baseDomain + location.pathname + location.search,
        "_blank"
      );
    }
  }

  _getTheme() {
    return theme.getThemeForTenant(window.EVENTIVE_CONFIG);
  }

  render() {
    const muiTheme = getMuiTheme(this._getTheme());

    let _StripeProvider = StripeProvider;
    if (!window.Stripe) {
      // Fallback if no Stripe connection
      _StripeProvider = PassThroughComponent;
    }

    const outerClassName = classNames(
      !this.props.noClass && "eventive-widget-container",
      window.EVENTIVE_CONFIG.sundance_login && "Eventive--Sundance"
    );
    const className = classNames(
      "eventive-widget-container",
      window.EVENTIVE_CONFIG.sundance_login && "Eventive--Sundance"
    );

    return (
      <EventListener target="window" onResize={this.handleResize}>
        <MuiThemeProvider muiTheme={muiTheme}>
          <_StripeProvider apiKey={process.env.STRIPE_PUBLIC_KEY}>
            <div className={outerClassName}>
              {this.state.showTicketsOverlay ? (
                <BuyTicketsOverlay
                  className={className}
                  appSettings={this.props.appSettings}
                  params={{ eventId: this.state.showTicketsOverlay }}
                  location={this.state.location}
                />
              ) : null}
              {this.state.showQuickOrderOverlay ? (
                <QuickOrderOverlay
                  className={className}
                  appSettings={this.props.appSettings}
                  params={{ eventId: this.state.showTicketsOverlay }}
                  location={this.state.location}
                />
              ) : null}
              {this.state.showPassesOverlay ? (
                <BuyPassesOverlay
                  className={className}
                  appSettings={this.props.appSettings}
                  params={{ code: this.state.showPassesOverlay }}
                  location={this.state.location}
                />
              ) : null}
              {this.state.showLoginOverlay ? (
                <Login
                  className={className}
                  modal={true}
                  location={this.state.location}
                />
              ) : null}
              {this.state.showPaymentUpdateOverlay ? (
                <EditPaymentMethodOverlay className={className} />
              ) : null}
              {this.state.showVirtualPlayerOverlay ? (
                <VirtualPlayerOverlay
                  params={{
                    virtualScreeningId: this.state.showVirtualPlayerOverlay
                  }}
                  className={className}
                />
              ) : null}
              {this.state.showScanTicketsOverlay ? (
                <ScanTicketsOverlay
                  className={className}
                  appSettings={this.props.appSettings}
                  params={{ eventId: this.state.showScanTicketsOverlay }}
                  location={this.state.location}
                />
              ) : null}
              {this.props.children}
            </div>
          </_StripeProvider>
        </MuiThemeProvider>
      </EventListener>
    );
  }
}

export default WidgetContainer;



// WEBPACK FOOTER //
// ./src/widget/widget-container.jsx